import { useCallback, useMemo } from 'react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_CALL_VOLUME } from 'src/graphql/callAnaltyics.graphql'
import {
  TrendChartView,
  type TrendBucket,
} from 'src/pages/AnalyticsPage/components/TrendChart'
import {
  buildTrendSeriesFromPalette,
  useAutoBucket,
  useRefetchOnKeyChange,
} from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import type { CallDirection, CallLogFiltersInput } from 'types/graphql'

const INFO_TEXT =
  'Call volume over time, bucketed by day, week, or month. One line per direction (Inbound + Outbound). The bucket adapts to your selected date range; you can override it with the toggle.'

// Visual palette + display order for the two direction lines. Greens for
// outbound (the "active" direction recruiters control), blues for inbound.
const DIRECTION_PALETTE = [
  { id: 'OUTBOUND' as CallDirection, label: 'Outbound', color: '#86EFAC' },
  { id: 'INBOUND' as CallDirection, label: 'Inbound', color: '#93C5FD' },
]

interface CallVolumePointShape {
  periodStart: string
  count: number
}
interface CallVolumeDirectionSeriesShape {
  direction: CallDirection
  points: ReadonlyArray<CallVolumePointShape>
}
interface CallVolumeShape {
  bucket: TrendBucket
  series: ReadonlyArray<CallVolumeDirectionSeriesShape>
}

export interface CallVolumeContainerProps {
  filters: CallLogFiltersInput
  /**
   * Bumped by the page-level Refresh button to trigger a manual refetch.
   * Mount value is 0 — the initial useQuery already fetches, so the first
   * change to this prop (i.e. ≥ 1) is what drives a refetch.
   */
  refreshKey: number
}

export const CallVolumeContainer = ({
  filters,
  refreshKey,
}: CallVolumeContainerProps) => {
  // Convert the date range to numeric timestamps once so `useAutoBucket`
  // sees stable primitive deps. Anything that calls `new Date(...)` inline
  // here would create fresh references on every render and re-fire the
  // hook's bucket-pick effect spuriously.
  const startMs = filters.dateRange?.gte
    ? new Date(filters.dateRange.gte).getTime()
    : 0
  const endMs = filters.dateRange?.lte
    ? new Date(filters.dateRange.lte).getTime()
    : Date.now()
  const [bucket, setBucket] = useAutoBucket(startMs, endMs)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_CALL_VOLUME, {
    variables: { input: { filters, bucket } },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const volume = data?.callVolume as CallVolumeShape | undefined

  const trendData = useMemo(() => {
    if (!volume) return undefined
    const series = buildTrendSeriesFromPalette(
      DIRECTION_PALETTE,
      volume.series,
      (s) => s.direction
    )
    return { bucket: volume.bucket, series }
  }, [volume])

  return (
    <TrendChartView
      title="Call Volume"
      info={INFO_TEXT}
      data={trendData}
      loading={loading}
      bucket={bucket}
      onBucketChange={setBucket}
      tooltipUnit="call"
      emptyMessage="No calls in this period"
    />
  )
}

export default CallVolumeContainer
